import React from 'react';
import { CheckCircle, Target } from 'lucide-react';
import { LeadGenProgress } from '../../../types';
import { CHAT_TEXT } from '../../../constants/chat';
import { SkylarqAvatar } from './SkylarqAvatar';

interface LeadProgressIndicatorProps {
  progressData: LeadGenProgress;
  entityType: 'people' | 'companies' | 'results';
}

/**
 * Progress indicator shown during lead generation
 * Displays animated dots, progress bar, and stats
 */
export const LeadProgressIndicator: React.FC<LeadProgressIndicatorProps> = ({
  progressData,
  entityType,
}) => {
  const entityLabel = entityType === 'people' ? 'people' : entityType === 'results' ? 'results' : 'companies';

  return (
    <div className="flex gap-3 animate-fadeIn">
      <SkylarqAvatar />
      <div className="flex-1 min-w-0 pt-0.5">
        <div className="flex items-center gap-2 mb-1">
          <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide">{CHAT_TEXT.BRAND_NAME_UPPER}</p>
        </div>
        <div className="bg-gray-50 rounded-xl p-3 border border-gray-100">
          {/* Message with animated dots */}
          <div className="flex items-center gap-2 mb-2">
            <span className="text-[13px] font-medium text-gray-700">
              {`Finding ${entityLabel}`}
            </span>
            <span className="flex gap-0.5">
              <span
                className="w-1.5 h-1.5 bg-gray-400 rounded-full"
                style={{ animation: 'chat-progress-dot 1.4s ease-in-out infinite' }}
              />
              <span
                className="w-1.5 h-1.5 bg-gray-400 rounded-full"
                style={{ animation: 'chat-progress-dot 1.4s ease-in-out 0.2s infinite' }}
              />
              <span
                className="w-1.5 h-1.5 bg-gray-400 rounded-full"
                style={{ animation: 'chat-progress-dot 1.4s ease-in-out 0.4s infinite' }}
              />
            </span>
          </div>

          {/* Progress bar */}
          <div className="h-1.5 bg-gray-200 rounded-full relative overflow-hidden mb-2">
            <div
              className="absolute inset-y-0 left-0 bg-gray-900 rounded-full transition-all duration-500 ease-out"
              style={{ width: `${Math.round(progressData.completion)}%` }}
            />
          </div>

          {/* Stats row */}
          <div className="flex items-center gap-4 text-[11px]">
            <div className="flex items-center gap-1.5">
              <CheckCircle className="w-3 h-3 text-gray-400" strokeWidth={2.5} />
              <span className="text-gray-500">
                <span className="font-semibold text-gray-700">{progressData.found}</span> found
              </span>
            </div>
            <div className="flex items-center gap-1.5">
              <Target className="w-3 h-3 text-gray-400" strokeWidth={2} />
              <span className="text-gray-500">
                <span className="font-medium text-gray-600">{progressData.analyzed}</span> analyzed
              </span>
            </div>
            <div className="ml-auto text-[11px] font-semibold text-gray-600">
              {Math.round(progressData.completion)}%
            </div>
          </div>
        </div>
      </div>

      {/* Animations */}
      <style>{`
        @keyframes chat-progress-dot {
          0%, 100% { opacity: 0.3; transform: scale(0.8); }
          50% { opacity: 1; transform: scale(1); }
        }
      `}</style>
    </div>
  );
};
